import React, { useEffect } from "react";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmButtonText?: string;
  confirmButtonColor?: "red" | "blue";
  isLoading?: boolean;
}

export default function ConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmButtonText,
  confirmButtonColor = "red",
  isLoading = false,
}: ConfirmationModalProps) {
  const { t } = useTranslation("common");
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const confirmButtonClasses =
    confirmButtonColor === "red"
      ? "bg-red-600 text-white hover:bg-red-700"
      : "bg-primary-600 text-white hover:bg-primary-700";

  return (
    <div
 className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleBackdropClick}
    >
 <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
 <div className="flex items-center justify-between mb-4">
 <h3 className="text-lg font-semibold text-secondary-900">{title}</h3>
          <button
            onClick={onClose}
 className="text-secondary-400 hover:text-secondary-600 transition-colors"
          >
 <X className="h-5 w-5" />
          </button>
        </div>

 <p className="text-secondary-600 mb-6">{message}</p>

 <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isLoading}
 className="px-4 py-2 border border-secondary-300 rounded-lg text-secondary-700 hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {t("cancel")}
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
 className={`px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${confirmButtonClasses}`}
          >
            {isLoading ? t("processing") : (confirmButtonText ?? t("confirm"))}
          </button>
        </div>
      </div>
    </div>
  );
}
